import React, { useLayoutEffect, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from './Overlay';

// ClampText - wrap to at most N lines, then an ellipsis at the real edge of the cell, with the
// full text in a tooltip (W-M7).
//
// Routine Work used to cut task names at a fixed character count, which truncated short words in
// a wide column and still overflowed a narrow one. CSS line-clamp cuts where the cell actually
// ends, at any width.

const CLAMP: Record<number, string> = {
  1: 'tw-line-clamp-1',
  2: 'tw-line-clamp-2',
  3: 'tw-line-clamp-3',
};

export interface ClampTextProps extends Omit<React.HTMLAttributes<HTMLElement>, 'children'> {
  children: React.ReactNode;
  /** Lines to show before the ellipsis. Default 2. */
  lines?: 1 | 2 | 3;
  /**
   * Full text for the tooltip. Derived from string/number children when omitted; pass it when
   * the children are elements.
   */
  title?: string;
  as?: 'span' | 'div';
}

function textOf(node: React.ReactNode): string | undefined {
  if (typeof node === 'string') return node || undefined;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) {
    const parts = node.map(textOf);
    return parts.every((p) => p !== undefined) ? parts.join('') : undefined;
  }
  return undefined;
}

export const ClampText: React.FC<ClampTextProps> = ({
  children,
  lines = 2,
  title,
  as = 'span',
  className = '',
  ...rest
}) => {
  const Comp = as;
  const ref = useRef<HTMLElement>(null);
  const [truncated, setTruncated] = useState(false);
  const fullText = title ?? textOf(children);

  // V2-012 / TBL-06: the shared tooltip (hover with a mouse, tap on touch) only when the text is
  // actually cut off. Re-checked when the cell resizes (column resize, window, iPad rotation).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !fullText) return;
    // Cut off vertically (the line clamp) or sideways (one long token in a narrow box).
    const check = () =>
      setTruncated(el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1);
    check();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [fullText, lines]);

  const body = (
    <Comp
      ref={ref as React.Ref<HTMLSpanElement & HTMLDivElement>}
      data-clamp={lines}
      // break-words so one long token (an asset number, a URL) wraps instead of overflowing.
      // No display utility here: tw-line-clamp-N sets display:-webkit-box itself, and a later
      // tw-block in the stylesheet silently cancelled the clamp (V2-012).
      className={`tw-min-w-0 tw-break-words ${CLAMP[lines]} ${className}`}
      title={truncated ? undefined : fullText}
      {...rest}
    >
      {children}
    </Comp>
  );

  if (!truncated || !fullText) return body;
  return (
    <OverlayTrigger
      placement="top"
      wrapperClassName="tw-block tw-min-w-0"
      overlay={<Tooltip className="tw-whitespace-pre-line tw-break-words">{fullText}</Tooltip>}
    >
      {body}
    </OverlayTrigger>
  );
};

export default ClampText;
